import { useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAssessment } from '../../../contexts/AssessmentContext'
import { calculateScores } from '../../../shared/utils/scoring'

function NewAssessment() {
  const { clientId } = useParams()
  const navigate = useNavigate()
  const { clients, loading, createAssessment } = useAssessment()

  const client = clients.find(c => c.id === clientId)

  const lastCompleted = useMemo(() => {
    if (!client?.assessments?.length) return null
    return client.assessments
      .filter(a => a.status === 'completed')
      .sort((a, b) => new Date(b.completedAt || b.completed_at) - new Date(a.completedAt || a.completed_at))[0] || null
  }, [client?.assessments])

  const isReassessment = !!lastCompleted

  // Compute score from responses for accuracy (cached scores may be stale after edits)
  const computedScores = useMemo(() => {
    if (!lastCompleted?.responses?.maturityAssessment) return null
    try { return calculateScores(lastCompleted.responses) } catch { return null }
  }, [lastCompleted?.responses])

  const lastScore = computedScores?.overall?.score ?? lastCompleted?.scores?.overall?.score
  const lastLevel = computedScores?.overall?.maturityLevel?.label || lastCompleted?.scores?.overall?.maturityLevel?.label || lastCompleted?.scores?.overall?.maturityLevel
  const lastDate = lastCompleted?.completedAt || lastCompleted?.completed_at

  const handleStartAssessment = async () => {
    const assessment = await createAssessment(clientId)
    if (assessment) {
      navigate(`/assessment/${assessment.id}`)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Client not found</h2>
        <button onClick={() => navigate('/clients')} className="btn-primary mt-4">Back to Clients</button>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto pt-8 pb-12">
      {/* Back */}
      <Link
        to={`/clients/${clientId}`}
        className="inline-flex items-center text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors mb-10"
      >
        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to {client.name}
      </Link>

      {/* Branded header block */}
      <div className="rounded-2xl overflow-hidden mb-6" style={{ background: 'linear-gradient(135deg, #012438 0%, #1A3A4F 100%)' }}>
        <div className="px-8 pt-10 pb-8">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: '#8AD0D6' }}>
            {isReassessment ? 'Reassessment Cycle' : 'Security Discovery Assessment'}
          </p>
          <h1 className="text-2xl font-bold text-white mb-2">
            {isReassessment
              ? `New Assessment for ${client.name}`
              : `Begin Discovery for ${client.name}`
            }
          </h1>
          <p className="text-sm leading-relaxed" style={{ color: '#8A9BAA' }}>
            {isReassessment
              ? 'Establish a new baseline to measure progress across your security program. Your previous assessment results will be preserved for trend analysis.'
              : 'This guided assessment evaluates your organization\'s security posture across business context, risk tolerance, and program maturity to produce an actionable advisory plan.'
            }
          </p>
        </div>

        {/* Step indicators */}
        <div className="flex items-center px-6 pb-6 pt-2">
          {['Business Scoping', 'Technology', 'Risk Appetite', 'Maturity'].map((step, i) => (
            <div key={step} className="flex items-center" style={{ flex: i < 3 ? 1 : 'none' }}>
              <div className="flex items-center justify-center" style={{ minWidth: '90px' }}>
                <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#8AD0D6' }}>
                  {step}
                </span>
              </div>
              {i < 3 && (
                <div className="flex-1 flex items-center justify-center">
                  <div className="w-1 h-1 rounded-full" style={{ backgroundColor: 'rgba(138, 208, 214, 0.25)' }} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Previous assessment card (reassessment only) */}
      {isReassessment && lastScore != null && (
        <div className="flex items-center justify-between px-6 py-5 mb-6 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <div>
            <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-wider font-medium mb-1">Previous Baseline</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {lastLevel} · {new Date(lastDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
            <button
              onClick={() => navigate(`/assessment/${lastCompleted.id}?mode=edit`)}
              className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Review & Edit
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-3xl font-bold text-gray-900 dark:text-white tabular-nums">{lastScore}<span className="text-lg text-gray-400">%</span></p>
            </div>
            <div className="w-1 h-10 rounded-full" style={{
              backgroundColor: lastScore >= 70 ? '#22C55E' : lastScore >= 50 ? '#EAB308' : lastScore >= 25 ? '#F97316' : '#EF4444'
            }} />
          </div>
        </div>
      )}

      {/* Deliverables */}
      <div className="mb-8">
        <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Assessment Outcomes</p>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Maturity Scoring', desc: 'CMMI-aligned scores across 14 security domains' },
            { label: 'Benchmark Analysis', desc: 'Industry and peer group comparison' },
            { label: 'Threat Context', desc: 'Industry-specific threat landscape and exposure' },
            { label: 'Enablement Plan', desc: 'Prioritized initiatives with cost-of-inaction' }
          ].map(d => (
            <div
              key={d.label}
              className="flex gap-3 p-3.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 shadow-sm"
            >
              <div className="w-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#8AD0D6' }} />
              <div>
                <p className="text-[13px] font-semibold text-gray-900 dark:text-white">{d.label}</p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{d.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Note */}
      <p className="text-xs text-gray-400 dark:text-gray-500 text-center mb-8">
        You may complete this assessment across multiple sessions.
      </p>

      {/* Actions */}
      <div className="flex items-center justify-center gap-4">
        <button
          onClick={() => navigate(`/clients/${clientId}?section=overview`)}
          className="px-5 py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleStartAssessment}
          className="px-8 py-3 text-sm font-semibold rounded-lg transition-all flex items-center gap-2 shadow-md"
          style={{ backgroundColor: '#012438', color: '#FFFFFF' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1A3A4F' }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#012438' }}
        >
          {isReassessment ? 'Begin Reassessment' : 'Begin Assessment'}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>
      </div>
    </div>
  )
}

export default NewAssessment
